import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { StarsButton } from '../components/StarsButton';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { logout } from '../lib/auth';

export function Settings() {
  const sub = useQuery({ queryKey: ['subscription'], queryFn: () => api.mySubscription() });

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <h1 className="font-display text-3xl uppercase tracking-wide">Settings</h1>

      <Section title="Premium">
        {sub.data?.isActive ? (
          <Card className="border-premium/40">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-chunky text-base text-premium">Premium active</div>
                <div className="text-xs text-text-dim mt-0.5">
                  Renews on{' '}
                  {sub.data.premiumUntil
                    ? new Date(sub.data.premiumUntil * 1000).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </div>
              </div>
              <span className="font-mono text-xs text-text-faint">200 ⭐/mo</span>
            </div>
            <div className="text-xs text-text-dim mt-3 leading-relaxed">
              Manage your subscription in Telegram → Settings → Subscriptions.
            </div>
          </Card>
        ) : (
          <Card className="border-premium/40 space-y-3">
            <div>
              <div className="font-chunky text-base text-premium">Go Premium · 200 ⭐/month</div>
              <ul className="space-y-1 text-xs text-text-dim mt-2">
                <li>• Unlimited private leagues (free: 3 max)</li>
                <li>• Advanced stats on your Profile</li>
                <li>• Premium tipster badge</li>
                <li>• Early access to new features</li>
              </ul>
            </div>
            <StarsButton
              productCode="PREMIUM_SUBSCRIPTION"
              label="Subscribe"
              stars={200}
              variant="primary"
              fullWidth
              invalidateKeys={[['me'], ['subscription']]}
              onSuccess={() => track('subscription_started', { stars_per_period: 200 })}
            />
            <div className="text-[10px] text-text-faint leading-relaxed">
              Recurring 30-day Telegram Stars subscription. Cancel anytime in Telegram → Settings.
            </div>
          </Card>
        )}
      </Section>

      <Section title="Notifications">
        <Card>
          <div className="text-text-dim text-sm">
            Toggle Telegram messages from <span className="font-mono text-trophy-gold">@PunditXIBot</span>
            : send <code className="font-mono">/notifications off</code> or{' '}
            <code className="font-mono">/notifications on</code> in the bot chat.
          </div>
        </Card>
      </Section>

      <Section title="Language">
        <Card>
          <div className="flex items-center justify-between">
            <span>English</span>
            <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
              Locked
            </span>
          </div>
        </Card>
      </Section>

      <Section title="About">
        <Card>
          <ul className="space-y-2 text-sm">
            <li>
              <Link className="text-trophy-gold" to="/about">
                About PunditXI →
              </Link>
            </li>
            <li>
              <Link className="text-trophy-gold" to="/privacy">
                Privacy policy →
              </Link>
            </li>
            <li>
              <Link className="text-trophy-gold" to="/terms">
                Terms of service →
              </Link>
            </li>
            <li className="text-text-faint font-mono text-xs">Bot · @PunditXIBot · v0.1</li>
            <li>
              <Link className="text-crimson font-mono text-xs" to="/admin">
                Internal admin →
              </Link>
            </li>
          </ul>
        </Card>
      </Section>

      <Button variant="danger" fullWidth onClick={() => logout()}>
        Sign out
      </Button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display-alt text-xs uppercase tracking-widest text-text-faint mb-1.5">
        {title}
      </h2>
      {children}
    </section>
  );
}
